<template>
    <div>
        <transition name="dialogfade">
            <k-dialog v-if="showDialog"
                v-bind:class="['edit-condition-dialog', 'disposition-wizard-dialog', { 'wizard-selected' : selectedWizard && selectedWizardSteps }, { 'clear-to-close': (selectedWizard === 'cleartoclose' || selectedWizard === 'conditionallyapprove') && selectedWizardSteps }]"
                :title="dialogTitle"
                @close="closeDialog">
                <div class="wizard-options__container" v-if="!selectedWizardSteps">
                    <div v-bind:class="['wizard-option', { 'option--disabled': option.disabled }]" v-for="option in wizardOptions" :key="option.title" @click="onWizardOptionClicked(option)">
                        <h2><span :class="option.iconClass"></span> {{ option.title }}</h2>
                    </div>
                </div>
                <div class="wizard__container" v-if="selectedWizardSteps">
                    <k-wizard :value="wizardStep" 
                        :items="selectedWizardSteps"
                        :disabled="true"
                        :key="wizardComponentKey"
                        @change="wizardStepChange"></k-wizard>

                    <conditionally-approve-wizard v-if="selectedWizard === 'conditionallyapprove'"
                        :wizard-step="wizardStep" 
                        :loan-id="loanId" 
                        :finish-clicked="finishClicked"
                        :earnest-money-deposit="EMD"
                        :fields="clearToCloseWizardFields"
                        :seller-contribution-fields="ctcSellerContFields"
                        :seller-contribution-total="ctcSellerContTotal"
                        :loan-VOLs="ctcLoanVOLs"
                        :loan-type="loanType"
                        :is-refi="isRefi"
                        @loanconditionallyapproved="onCompleteConditionalApproval"
                        @processorselected="setClearToFinish"
                        @requestfailed="onRequestFailed"
                        @setcurrentstepcleared="setCurrentStepCleared"  
                        class="steps__container conditionally-approve"></conditionally-approve-wizard>

                    <suspend-wizard v-if="selectedWizard === 'suspend'"
                        :wizard-step="wizardStep" 
                        :loan-id="loanId" 
                        :finish-clicked="finishClicked"
                        @loansuspended="onCompleteLoanSuspend"
                        @processorselected="setClearToFinish"
                        @requestfailed="onRequestFailed"
                        class="steps__container conditionally-approve"></suspend-wizard>

                    <deny-wizard v-if="selectedWizard === 'deny'"
                        :wizard-step="wizardStep" 
                        :loan-id="loanId" 
                        :finish-clicked="finishClicked"
                        @setcurrentstepcleared="setCurrentStepCleared"  
                        @allstepsvalidated="setClearToFinish" 
                        @loandenied="onCompleteLoanDeny" 
                        @requestfailed="onRequestFailed"
                        @creditreasons="updateDenyWizardSteps"
                        class="steps__container deny"></deny-wizard>

                    <restructure-wizard v-if="selectedWizard === 'sendbackforrestructure'"
                        :wizard-step="wizardStep" 
                        :loan-id="loanId" 
                        :finish-clicked="finishClicked"
                        @cleartofinish="setClearToFinish"
                        @requestfailed="onRequestFailed"
                        @loanrestructured="onCompleteLoanRestructure"
                        class="steps__container restructure"></restructure-wizard>

                    <clear-to-close-wizard v-if="selectedWizard === 'cleartoclose' && selectedWizardSteps"
                        :wizard-step="wizardStep"
                        :loan-id="loanId"
                        :finish-clicked="finishClicked"
                        :earnest-money-deposit="EMD"
                        :fields="clearToCloseWizardFields"
                        :seller-contribution-fields="ctcSellerContFields"
                        :seller-contribution-total="ctcSellerContTotal"
                        :loan-VOLs="ctcLoanVOLs"
                        :attachments-source="attachments"
                        :all-documents="documents"
                        :loan-type="loanType"
                        :is-refi="isRefi"
                        @setcurrentstepcleared="setCurrentStepCleared"  
                        @cleartofinish="setClearToFinish"
                        @requestfailed="onRequestFailed"
                        @loanclearedtoclose="onCompleteLoanClearToClose"
                        @documentsupdated="onDocumentsUpdated"></clear-to-close-wizard>
                </div>

                <k-dialog-actions v-if="selectedWizard && selectedWizardSteps">
                    <div class="wizard__step-actions">
                        <p class="current-step--display">{{ `Step ${wizardStep + 1} of ${selectedWizardSteps.length}`}}</p>
                        <k-button class="step__btn btn--prev" @click="loadPrevStep" :disabled="wizardStep === 0 || finishBtnDisabled">Previous</k-button>
                        <k-button class="step__btn btn--next" @click="loadNextStep" v-if="!isLastStep" :disabled="!selectedWizardSteps[wizardStep].valid">Next</k-button>
                        <k-button class="step__btn disposition__btn" @click="onFinishClicked" v-if="isLastStep" :disabled="!clearToFinish || finishBtnDisabled">Finish</k-button>
                    </div>
                </k-dialog-actions>

                <div class="conditions__loader-container" v-if="isLoading">
                    <loader :size="'large'" :type="'infinite-spinner'"></loader>
                </div>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
          <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
            <p class="error-dialog__msg">{{ errorMsg }}</p>
          </k-dialog>
        </transition>
    </div>
</template>

<script>
    import auth from '../authConfig.js';
    import apiService from '../api/api.service.js';

    import cloneDeep from 'lodash.clonedeep';

    import { Dialog, DialogActionsBar, Window } from '@progress/kendo-vue-dialogs';
    import { MultiSelect, DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Stepper } from '@progress/kendo-vue-layout';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Button } from '@progress/kendo-vue-buttons';
    import { filterBy } from '@progress/kendo-data-query';

    import conditionallyApproveWizard from './conditionallyApproveWizard.vue';
    import suspendWizard from './suspendWizard.vue';
    import denyWizard from './denyWizard.vue';
    import restructureWizard from './restructureWizard.vue';
    import clearToCloseWizard from './clearToCloseWizard.vue';

    export default {
        props: ['attachments', 'documents', 'loanId', 'showDialog'],
        emits: ['completecleartoclose', 'completeconditionalapproval', 'completedeny', 'completerestructure', 'completesuspend', 'dialogclosed', 'updatedocuments'],
        data() {
            return {
                clearToCloseWizardFields: [],
                clearToCloseWizardFieldsList: ['19', '912', 'URLA.X151', 'L128', '4795', '202', '4667', '141', '1091', '4668', '1095', '1106', '4669', '1115', '1646', '4670', '1647'],
                clearToFinish: false,
                ctcAllWizardSteps: [
                    { label: 'Run AUS Findings', componentName: 'runAUSFindings', valid: true },
                    { label: 'Populate Closing Conditions', componentName: 'populateClosingConditions', valid: false }
                ],
                ctcFHAWizardSteps: [
                    { label: 'FHA Summary', componentName: 'fhaSummary', valid: true },
                    { label: 'FHA Docs', componentName: 'fhaDocs', valid: false }
                ],
                ctcFormWizardSteps: [
                    { label: 'Document Dates', componentName: 'documentDates', valid: true },
                    { label: 'Underwriter Summary', componentName: 'underwriterSummary', valid: false },
                ],
                ctcLastWizardStep: [
                    { label: 'Assign Processor / File Notes', componentName: 'confirmProcessor', valid: true },
                ],
                ctcMIWizardSteps: [
                    { label: 'Review Quote', componentName: 'reviewQuote', valid: true },
                    { label: 'Order MI', componentName: 'orderMI', valid: true }
                ],
                ctcSellerContFields: [],
                ctcSellerContTotal: 0,
                ctcSellerContPairs: {
                    field202: '141',
                    field1091: '1095',
                    field1106: '1115',
                    field1646: '1647'
                },
                ctcLoanVOLs: [],
                ctcVAWizardSteps: [
                    { label: 'VA Summary', componentName: 'vaSummary', valid: true },
                    { label: 'VA Docs', componentName: 'vaDocs', valid: false }
                ],
                dialogTitle: 'Disposition Options', 
                EMD: 0,
                errorMsg: '',
                errorTitle: '',
                finishBtnDisabled: false,
                finishClicked: false,
                isLoading: false,
                isRefi: false,
                loanType: '',
                milestones: [],
                selectedWizard: '',
                selectedWizardSteps: null,
                showErrorDialog: false,
                wizardComponentKey: 0,
                wizardOptions: [
                    { title: 'Conditionally Approve', wizard: 'conditionallyapprove', iconClass: 'k-icon k-i-track-changes-accept', disabled: false },
                    { title: 'Clear to Close', wizard: 'cleartoclose', iconClass: 'k-icon k-i-check-circle k-i-checkmark-circle', disabled: false },
                    { title: 'Suspend', wizard: 'suspend', iconClass: 'k-icon k-i-warning k-i-exception k-i-error', disabled: false },
                    { title: 'Deny', wizard: 'deny', iconClass: 'k-icon k-i-close-circle k-i-x-circle', disabled: false },
                    { title: 'Send Back For Restructure', wizard: 'sendbackforrestructure', iconClass: 'k-icon k-i-undo', disabled: false }
                ],
                wizardStep: 0,
                wizardSteps: {
                    conditionallyapprove: [
                        { label: 'Populate Closing Conditions', componentName: 'populateClosingConditions', valid: false },
                        { label: 'Assign Processor / File Notes', componentName: 'confirmProcessor', valid: true }
                        //{ label: 'Review & Save', componentName: 'confirmAndSave', valid: true }
                    ],
                    suspend: [
                        { label: 'File Notes', componentName: 'completeFileNotes', valid: true },
                        { label: 'Assign Processor', componentName: 'confirmProcessor', valid: true },
                        { label: 'Review & Save', componentName: 'confirmAndSave', valid: true }
                    ],
                    deny: [
                        { label: 'Denial Information', componentName: 'denialInformation', valid: false },
                        { label: 'Statement of Denial', componentName: 'statementOfDenial', valid: false },
                        { label: 'Fact Act Disclosure', componentName: 'factActDisclosure', valid: true },
                        { label: 'File Notes', componentName: 'completeFileNotes', valid: true },
                        { label: 'Review & Save', componentName: 'confirmAndSave', valid: true }
                    ],
                    sendbackforrestructure: [
                        { label: 'File Notes', componentName: 'completeFileNotes', valid: true },
                        { label: 'Review & Save', componentName: 'confirmAndSave', valid: true }
                    ]
                }
            }
        },
        computed: {
            isLastStep() {
                return this.selectedWizardSteps.length - 1 === this.wizardStep;
            }
        },
        mounted() {

        },
        watch: {
            showDialog(val) {
                if (val) {
                    let isSuspended = sessionStorage.getItem('issuspended');
                    this.wizardOptions[2].disabled = isSuspended === 'true';

                    let isDenied = sessionStorage.getItem('isdenied');
                    this.wizardOptions[3].disabled = isDenied === 'true';
                } else {
                    this.selectedWizardSteps = null;
                    this.selectedWizard = '';
                    this.dialogTitle = 'Disposition Options';
                    this.isLoading = false;
                    this.wizardStep = 0;
                    this.finishClicked = false;
                    this.clearToFinish = false;
                    this.finishBtnDisabled = false;
                    this.clearToCloseWizardFields = [];
                    this.ctcSellerContFields = [];
                    this.ctcSellerContTotal = 0;
                    this.EMD = 0;
                    this.isRefi = false;

                    let closeBtn = document.querySelector('.k-window-titlebar-actions');
                    closeBtn?.classList.remove('titlebar--hide');
                }
            }
        },
        methods: {
            closeDialog() {
                this.$emit('dialogclosed');
            },
            loadNextStep() {
                this.wizardStepChange({ value: this.wizardStep + 1 })
            },
            loadPrevStep() {
                this.wizardStepChange({ value: this.wizardStep - 1 })
            },
            onCompleteConditionalApproval(notification) {
                this.$emit('completeconditionalapproval', notification);
            },
            onCompleteLoanClearToClose(notification) {
                this.$emit('completecleartoclose', notification);
            },
            onCompleteLoanDeny(notification) {
                this.$emit('completedeny', notification);
            },
            onCompleteLoanRestructure(notification) {
                this.$emit('completerestructure', notification);
            },
            onCompleteLoanSuspend(notification) {
                this.$emit('completesuspend', notification);
            },
            onDocumentsUpdated() {
                this.$emit('updatedocuments');
            },
            onFinishClicked() {
                this.finishClicked = true;
                this.finishBtnDisabled = true;

                let closeBtn = document.querySelector('.k-window-titlebar-actions');
                closeBtn?.classList.add('titlebar--hide');
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.toggleErrorDialog();
            },
            onRequestFailed() {
                this.finishClicked = false;
                this.finishBtnDisabled = false;

                let closeBtn = document.querySelector('.k-window-titlebar-actions');
                closeBtn?.classList.remove('titlebar--hide');
            },
            async onWizardOptionClicked(option) {
                this.dialogTitle = option.title;

                this.selectedWizard = option.wizard;

                if (this.selectedWizard === 'deny') {
                    return this.selectedWizardSteps = this.wizardSteps[this.selectedWizard].filter(step => step.label !== 'Fact Act Disclosure');
                }

                if (this.selectedWizard === 'conditionallyapprove') {
                    this.isLoading = true;
                    this.loanType = sessionStorage.getItem('loantype');

                    await this.processCTCWizardFields();

                    this.isLoading = false;
                }

                if (this.selectedWizard === 'cleartoclose') {
                    this.isLoading = true;
                    let ctcWizardSteps = [];

                    let hasMI = sessionStorage.getItem('hasmi');
                    this.loanType = sessionStorage.getItem('loantype');
                    
                    if (this.loanType && this.loanType !== 'FHA' && hasMI && +hasMI > 0) {
                        ctcWizardSteps.push(this.ctcMIWizardSteps);
                    } 

                    ctcWizardSteps.push(this.ctcAllWizardSteps);
                    
                    await this.processCTCWizardFields();

                    ctcWizardSteps.push(this.ctcFormWizardSteps);

                    if (this.loanType === 'FHA') {
                        ctcWizardSteps.push(this.ctcFHAWizardSteps);
                  }

                    //** WIP - VA CTC Wizard */
                    //if (this.loanType === 'VA') {
                    //    ctcWizardSteps.push(this.ctcVAWizardSteps);
                    //}

                    ctcWizardSteps.push(this.ctcLastWizardStep);

                    this.isLoading = false;

                    return this.selectedWizardSteps = cloneDeep(ctcWizardSteps.flat());
                }

                this.selectedWizardSteps = cloneDeep(this.wizardSteps[this.selectedWizard]);
            },
            async processCTCWizardFields() {
                this.clearToCloseWizardFields = await this.$store.dispatch('getLoanFields', this.clearToCloseWizardFieldsList) ?? [];

                if (this.clearToCloseWizardFields.length === 0) {
                    this.dialogTitle = 'Disposition Options';
                    this.selectedWizard = '';
                    return this.onRequestError('Failed to Initialize Wizard');
                }

                let loanPurposeField = this.clearToCloseWizardFields?.find(field => field.fieldId === '19');

                if (loanPurposeField && loanPurposeField.value?.toLowerCase().includes('refi')) {
                    this.isRefi = true;
                }

                let emdField = this.clearToCloseWizardFields?.find(field => field.fieldId === 'L128');
                let emdFieldAlt = this.clearToCloseWizardFields?.find(field => field.fieldId === 'URLA.X151');
                
                if (emdField && emdField.value && +emdField.value > 0) {
                    this.EMD = +emdField.value;
                }

                if (emdFieldAlt && emdFieldAlt.value && +emdFieldAlt.value > 0) {
                    this.EMD += +emdFieldAlt.value;
                }

                let sellerContField = this.clearToCloseWizardFields?.find(field => field.fieldId === '4795');

                if (sellerContField && sellerContField.value && +sellerContField.value > 0) {
                    this.ctcSellerContFields.push(sellerContField);
                    this.ctcSellerContTotal += +sellerContField.value;
                }

                let sellerCreditFields = this.clearToCloseWizardFields.filter(field => field.value === 'SellerCredit');
                
                if (sellerCreditFields.length > 0) {
                    sellerCreditFields.forEach(field => {
                        let pairField = this.ctcSellerContPairs[`field${field.fieldId}`];
                        let match = this.clearToCloseWizardFields.find(field => field.fieldId === pairField);

                        if (match && match.value && +match.value > 0) {
                            this.ctcSellerContFields.push(field);
                            this.ctcSellerContFields.push(match);
                            this.ctcSellerContTotal += +match.value;
                        }
                    });
                }

                let fileVOLs = sessionStorage.getItem('loanvols');

                if (fileVOLs) {
                    this.ctcLoanVOLs = JSON.parse(fileVOLs);
                }
            },
            setClearToFinish(clearToFinish) {
                this.clearToFinish = clearToFinish;
            },
            setCurrentStepCleared(setting, step) {
                this.selectedWizardSteps[step].valid = setting;
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if (!this.showErrorDialog) {
                    this.errorMsg = '';
                    this.errorTitle = '';
                }
            },
            updateDenyWizardSteps(creditReasons) {
                if (creditReasons) {
                    this.selectedWizardSteps = cloneDeep(this.wizardSteps[this.selectedWizard]);
                } else {
                    this.selectedWizardSteps = this.wizardSteps[this.selectedWizard].filter(step => step.label !== 'Fact Act Disclosure');
                }

                this.wizardComponentKey += 1;
            },
            wizardStepChange(evt) {
                this.wizardStep = evt.value;
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-dialog-actions': DialogActionsBar,
            'k-wizard': Stepper,
            'loader': Loader,
            'k-dialog-actions': DialogActionsBar,
            'k-button': Button,
            'k-dropdown': DropDownList,
            'conditionally-approve-wizard': conditionallyApproveWizard,
            'suspend-wizard': suspendWizard,
            'deny-wizard': denyWizard,
            'restructure-wizard': restructureWizard,
            'clear-to-close-wizard': clearToCloseWizard
        }
    }
</script>

<style>
    @import '/src/assets/css/editConditionDialog.css';
    @import '/src/assets/css/dispositionMenu.css';
</style>
